// ===================================================================
// components/ConversationGame.tsx
// Exercice de conversations interactives — style Sentier
// ===================================================================
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from './ThemeContext';
import FloatingBackButton from './FloatingBackButton';
import { CONVERSATIONS_BY_LANGUAGE, type Conversation, type ConversationOption, type LexiconEntry } from '../data/conversationsData';
import type { LanguageCode } from '../types/types';
import { getWordTranslation, getSentenceTranslation } from '../utils/localizedContent';
import './ConversationGame.css';
import TribalBorder from './TribalBorder';

import { EmojiText, Picto } from './common/Picto';
interface ConversationGameProps {
  languageCode: LanguageCode;
  onBack: () => void;
  initialConversationId?: string;
  onComplete?: () => void;
}

type Screen = 'playing' | 'results';

type ChatEntry =
  | { type: 'character'; text: string; translation: string; emoji: string; name: string; stepIdx: number }
  | { type: 'hint'; text: string; stepIdx: number }
  | { type: 'user'; text: string; translation: string; isCorrect: boolean; stepIdx: number };


function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ConversationGame: React.FC<ConversationGameProps> = ({ languageCode, onBack, initialConversationId, onComplete }) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const conversations = CONVERSATIONS_BY_LANGUAGE[languageCode] ?? [];

  const [screen, setScreen] = useState<Screen>('playing');
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [pickedOption, setPickedOption] = useState<ConversationOption | null>(null);
  const [pickedReaction, setPickedReaction] = useState<string | null>(null);
  const [chatEntries, setChatEntries] = useState<ChatEntry[]>([]);
  const [shuffledOptions, setShuffledOptions] = useState<ConversationOption[][]>([]);
  const [showTranslation, setShowTranslation] = useState(false);
  const [showLexicon, setShowLexicon] = useState(false);

  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  const startConversation = useCallback((conv: Conversation) => {
    const firstStep = conv.steps[0];
    const initialEntries: ChatEntry[] = [{
      type: 'character',
      text: firstStep.message,
      translation: getWordTranslation(firstStep, i18n.language),
      emoji: conv.character.emoji,
      name: conv.character.name,
      stepIdx: 0,
    }];
    if (firstStep.hint) initialEntries.push({ type: 'hint', text: firstStep.hint, stepIdx: 0 });
    setSelected(conv);
    setShuffledOptions(conv.steps.map((s: import('../data/conversationsData').ConversationStep) => shuffle(s.options)));
    setStepIndex(0);
    setScore(0);
    setPickedOption(null);
    setPickedReaction(null);
    setChatEntries(initialEntries);
    setScreen('playing');
  }, [i18n.language]);

  const handleAnswer = useCallback((option: ConversationOption) => {
    if (pickedOption !== null || !selected) return;
    setPickedOption(option);
    const currentStep = selected.steps[stepIndex];
    if (option.isCorrect) setScore(prev => prev + 1);
    setPickedReaction(currentStep.correctReaction ?? null);
    setChatEntries(prev => [...prev, {
      type: 'user',
      text: option.text,
      translation: getWordTranslation(option, i18n.language),
      isCorrect: option.isCorrect,
      stepIdx: stepIndex,
    }]);
    const delay = option.isCorrect ? 1800 : 2800;
    autoAdvanceTimer.current = setTimeout(() => {
      const nextIndex = stepIndex + 1;
      if (nextIndex >= selected.steps.length) {
        try {
          localStorage.setItem(`${languageCode}-conv-${selected.id}`, JSON.stringify(selected.steps.length));
          window.dispatchEvent(new CustomEvent('xpUpdated'));
          onComplete?.();
        } catch {}
        setScreen('results');
      } else {
        const nextStep = selected.steps[nextIndex];
        setChatEntries(prev => {
          const entries: ChatEntry[] = [...prev, {
            type: 'character',
            text: nextStep.message,
            translation: getWordTranslation(nextStep, i18n.language),
            emoji: selected.character.emoji,
            name: selected.character.name,
            stepIdx: nextIndex,
          }];
          if (nextStep.hint) entries.push({ type: 'hint', text: nextStep.hint, stepIdx: nextIndex });
          return entries;
        });
        setStepIndex(nextIndex);
        setPickedOption(null);
        setPickedReaction(null);
        setShowTranslation(false);
      }
    }, delay);
  }, [pickedOption, selected, stepIndex, i18n.language]);

  const handleReplay = useCallback(() => {
    if (selected) startConversation(selected);
  }, [selected, startConversation]);

  useEffect(() => () => { if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current); }, []);

  // Auto-start a specific conversation when coming from the trail
  useEffect(() => {
    if (!initialConversationId) return;
    const conv = conversations.find(c => c.id === initialConversationId);
    if (conv) startConversation(conv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 150);
  }, [chatEntries]);

  // ── Playing screen ────────────────────────────────────────────────
  if (screen === 'playing' && selected) {
    const step = selected.steps[stepIndex];
    const progress = (stepIndex / selected.steps.length) * 100;
    const lexicon: LexiconEntry[] = selected.lexicon ?? [];

    return (
      <div className="conv-play" data-theme={theme}>
        <TribalBorder />
        {/* Header */}
        <div className="conv-play-header">
          <div className="conv-play-header__top">
            <FloatingBackButton onBack={onBack} mode="inline" variant="minimal" />
            <div className="conv-play-header__bar-track">
              <motion.div
                className="conv-play-header__bar-fill"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
            <span className="conv-play-header__score">{score}/{selected.steps.length}</span>
          </div>
          <div className="conv-play-header__bottom">
            <span>{selected.title}</span>
            <span>{t('conversation.turnCounter', { current: stepIndex + 1, total: selected.steps.length })}</span>
          </div>
        </div>

        {/* Chat */}
        <div className="conv-chat">
          {chatEntries.map((entry, i) => {
            const isPast = entry.stepIdx < stepIndex;
            const opacity = isPast ? 0.6 : 1;

            if (entry.type === 'character') {
              const isCurrent = entry.stepIdx === stepIndex;
              const hideTranslation = isCurrent && !showTranslation && !pickedOption;
              return (
                <div key={i} className="conv-bubble-char" style={{ opacity }}>
                  <div
                    className="conv-bubble-char__avatar"
                    style={{ width: isCurrent ? 44 : 32, height: isCurrent ? 44 : 32, fontSize: isCurrent ? 22 : 16 }}
                  >
                    <EmojiText text={entry.emoji} />
                  </div>
                  <div style={{ flex: 1 }}>
                    {isCurrent && <div className="conv-bubble-char__name">{entry.name}</div>}
                    <motion.div
                      key={`char-${i}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="conv-bubble-char__msg"
                    >
                      <p className={`conv-bubble-char__text ${!isCurrent ? 'conv-bubble-char__text--small' : ''}`}>
                        {entry.text}
                      </p>
                      {!hideTranslation && (
                        <p className="conv-bubble-char__trans">{entry.translation}</p>
                      )}
                    </motion.div>
                    {isCurrent && !pickedOption && (
                      <button
                        className="conv-trans-toggle"
                        onClick={() => setShowTranslation(v => !v)}
                      >
                        <EmojiText text={(showTranslation ? t('conversation.hideTranslation') : t('conversation.showTranslation')) as string} />
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            if (entry.type === 'hint') {
              return (
                <div key={i} className="conv-hint" style={{ opacity }}>
                  <Picto name="lampe" /> {entry.text}
                </div>
              );
            }

            if (entry.type === 'user') {
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity, x: 0 }}
                  transition={{ duration: 0.25 }}
                  className="conv-bubble-user"
                >
                  <div className={`conv-bubble-user__msg ${entry.isCorrect ? 'conv-bubble-user__msg--correct' : 'conv-bubble-user__msg--wrong'}`}>
                    <p className="conv-bubble-user__text">{entry.text}</p>
                    <p className="conv-bubble-user__trans">{entry.translation}</p>
                  </div>
                </motion.div>
              );
            }
            return null;
          })}
          <div ref={chatBottomRef} style={{ height: 4 }} />
        </div>

        {/* Options / feedback panel */}
        <div className="conv-panel">
          <AnimatePresence>
            {!pickedOption ? (
              <motion.div
                key={`options-${stepIndex}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <p className="conv-panel__hint">{t('conversation.howToRespond')}</p>
                <div className="conv-options">
                  {(shuffledOptions[stepIndex] ?? step.options).map((option: ConversationOption, idx: number) => (
                    <motion.button
                      key={idx}
                      className="conv-option"
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleAnswer(option)}
                    >
                      <span className="conv-option__letter">{['A','B','C'][idx]}</span>
                      <div>
                        <p className="conv-option__text">{option.text}</p>
                        <p className="conv-option__trans">{getWordTranslation(option, i18n.language)}</p>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="feedback"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="conv-feedback"
              >
                {!pickedOption.isCorrect && (
                  <div className="conv-feedback__correct-answer">
                    <p className="conv-feedback__label">{t('conversation.correctAnswerLabel')}</p>
                    <p className="conv-feedback__answer-text">
                      {step.options.find((o: ConversationOption) => o.isCorrect)?.text}
                    </p>
                    <p className="conv-feedback__answer-trans">
                      {(() => {
                        const correct = step.options.find((o: ConversationOption) => o.isCorrect);
                        return correct ? getWordTranslation(correct, i18n.language) : null;
                      })()}
                    </p>
                  </div>
                )}
                <div className={`conv-feedback__bar ${pickedOption.isCorrect ? 'conv-feedback__bar--correct' : 'conv-feedback__bar--wrong'}`}>
                  <span className="conv-feedback__bar-icon">
                    <EmojiText text={pickedOption.isCorrect ? '✅' : '❌'} />
                  </span>
                  <p className={`conv-feedback__bar-text ${pickedOption.isCorrect ? 'conv-feedback__bar-text--correct' : 'conv-feedback__bar-text--wrong'}`}>
                    {pickedOption.isCorrect
                      ? (pickedReaction ?? t('conversation.goodAnswerDefault'))
                      : t('conversation.notQuiteRight')}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Lexicon toggle */}
          {lexicon.length > 0 && (
            <div className="conv-lexicon-wrap">
              <button
                className="conv-lexicon-toggle"
                onClick={() => setShowLexicon(v => !v)}
              >
                <Picto name="livre" /> {showLexicon ? t('conversation.closeLexicon') : t('conversation.openLexicon')}
              </button>
              <AnimatePresence>
                {showLexicon && (
                  <motion.div
                    className="conv-lexicon"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    {lexicon.map((entry, i) => (
                      <div key={i} className="conv-lexicon__row">
                        <span className="conv-lexicon__bambara">{entry.bambara}</span>
                        <span className="conv-lexicon__french">{getSentenceTranslation({ french: entry.french, english: entry.english }, i18n.language)}</span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Results screen ────────────────────────────────────────────────
  if (screen === 'results' && selected) {
    const total = selected.steps.length;
    const pct = total > 0 ? Math.round((score / total) * 100) : 0;
    let emoji = '😔';
    let message = t('conversation.results.keepPracticing');
    if (pct === 100)      { emoji = '🏆'; message = t('conversation.results.perfect'); }
    else if (pct >= 80)   { emoji = '🌟'; message = t('conversation.results.excellent'); }
    else if (pct >= 60)   { emoji = '👍'; message = t('conversation.results.wellDone'); }
    else if (pct >= 40)   { emoji = '📖'; message = t('conversation.results.keepLearning'); }

    return (
      <div className="conv-results" data-theme={theme}>
        <motion.div
          className="conv-results__card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="conv-results__emoji"><EmojiText text={emoji} /></div>
          <h2 className="conv-results__title">{selected.title}</h2>
          <p className="conv-results__sub">{t('conversation.results.finished')}</p>

          <div
            className="conv-results__score-ring"
            style={{
              background: `conic-gradient(var(--color-accent, var(--gold-primary)) ${pct * 3.6}deg, rgba(200,112,64,0.12) 0)`
            }}
          >
            <div className="conv-results__score-inner">
              <span className="conv-results__score-num">{score}</span>
              <span className="conv-results__score-denom">/ {total}</span>
            </div>
          </div>

          <p className="conv-results__msg">{message}</p>

          <div className="conv-results__actions">
            <button className="conv-results__btn--primary" onClick={handleReplay}>
              <EmojiText text={t('quiz.replay') as string} />
            </button>
            <button className="conv-results__btn--secondary" onClick={onBack}>
              {t('conversation.results.backToTrail')}
            </button>
            <FloatingBackButton onBack={onBack} mode="inline" variant="ghost" text={t('conversation.results.backToMenu') as string} />
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default ConversationGame;
